import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  chatCardClient,
  isRecord,
  text,
  type ChatCardClient,
} from "@/features/portfolio-analysis/lib/chat-card-client";

export interface DecisionChainSeed {
  clientId: string | null;
  clientQuery: string | null;
  adviceDate: string | null;
}

export interface DecisionChainEntry {
  toolCallId: string;
  seed: DecisionChainSeed;
  client: ChatCardClient | null;
  adviceId: string | null;
}

export function openDecisionChain(toolCallId: string, seed: DecisionChainSeed): void {
  STORE.open({ toolCallId, seed, client: null, adviceId: null });
}

export function decisionChainAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasDecisionChain(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useDecisionChain(toolCallId: string): DecisionChainEntry | null {
  return STORE.useEntry(toolCallId);
}

export function useDecisionChainAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useDecisionChainStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseChainClient(toolCallId: string, client: ChatCardClient): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, client, adviceId: null }));
}

export function chooseChainAdvice(
  toolCallId: string,
  client: ChatCardClient,
  adviceId: string,
): void {
  const known = STORE.read(toolCallId);
  if (
    known?.adviceId === adviceId &&
    known.client?.kind === client.kind &&
    known.client.subjectId === client.subjectId
  ) {
    return;
  }
  STORE.update(toolCallId, (entry) => ({ ...entry, client, adviceId }));
}

function narrowEntry(value: unknown): DecisionChainEntry | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const seed = isRecord(value.seed) ? value.seed : {};
  const client = chatCardClient(value.client);
  return {
    toolCallId: value.toolCallId,
    seed: {
      clientId: text(seed.clientId),
      clientQuery: text(seed.clientQuery),
      adviceDate: text(seed.adviceDate),
    },
    client,
    adviceId: client === null ? null : text(value.adviceId),
  };
}

const STORE = toolCallStore<DecisionChainEntry>({
  key: "simon.documents.decisionChain",
  narrow: narrowEntry,
  stage: (entry) => (entry.adviceId === null ? "awaiting" : "done"),
});
